import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { 
  Sun, Moon, Star, Sparkles, Eye, AlertTriangle, ZoomIn, 
  Radio, Thermometer, Video, Target, Crown, Lock,
  Gauge, ChevronRight, Zap, Layers, Crosshair
} from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";

// 3D Filter Card Component
const Filter3DCard = ({ icon: Icon, label, active, locked, onClick, color, description }) => {
  const { theme } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.button
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ scale: locked ? 1 : 1.02, y: locked ? 0 : -2 }}
      whileTap={{ scale: locked ? 1 : 0.98 }}
      onClick={onClick}
      disabled={locked}
      className={`
        relative w-full p-4 rounded-2xl transition-all duration-300 overflow-hidden
        ${locked ? 'cursor-not-allowed' : 'cursor-pointer'}
      `}
      style={{
        background: active 
          ? `linear-gradient(145deg, ${color}15 0%, ${color}05 100%)`
          : 'linear-gradient(145deg, rgba(30,35,50,0.8) 0%, rgba(15,20,35,0.9) 100%)',
        boxShadow: active
          ? `0 10px 40px ${color}30, inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -1px 0 rgba(0,0,0,0.2)`
          : isHovered && !locked
            ? '0 10px 30px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)'
            : '0 4px 15px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.03)',
        border: active 
          ? `1px solid ${color}50`
          : '1px solid rgba(255,255,255,0.05)',
        transform: active ? 'translateZ(10px)' : 'translateZ(0)',
        opacity: locked ? 0.5 : 1
      }}
      data-testid={`filter-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      {/* 3D Highlight Effect */}
      <div 
        className="absolute inset-0 rounded-2xl pointer-events-none"
        style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, transparent 40%)',
          opacity: isHovered ? 1 : 0.5
        }}
      />
      
      {/* Glow Effect when Active */}
      {active && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{
            background: `radial-gradient(circle at 30% 30%, ${color}20 0%, transparent 60%)`,
          }}
        />
      )}

      <div className="relative flex items-center gap-4">
        {/* 3D Icon Container */}
        <div 
          className="relative w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300"
          style={{
            background: active 
              ? `linear-gradient(145deg, ${color}30 0%, ${color}10 100%)`
              : 'linear-gradient(145deg, rgba(50,55,70,0.8) 0%, rgba(30,35,50,0.8) 100%)',
            boxShadow: active
              ? `0 4px 15px ${color}40, inset 0 1px 0 rgba(255,255,255,0.2)`
              : '0 4px 10px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)',
            border: `1px solid ${active ? color + '40' : 'rgba(255,255,255,0.05)'}`
          }}
        >
          <Icon 
            className="w-6 h-6 transition-all duration-300" 
            style={{ 
              color: active ? color : theme.colors.textMuted,
              filter: active ? `drop-shadow(0 0 8px ${color})` : 'none'
            }}
          />
          
          {/* Active Indicator Ring */}
          {active && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="absolute inset-0 rounded-xl"
              style={{
                border: `2px solid ${color}`,
                boxShadow: `0 0 15px ${color}50`
              }}
            />
          )}
        </div>

        {/* Label & Description */}
        <div className="flex-1 text-left">
          <div className="flex items-center gap-2">
            <span 
              className="font-unbounded font-bold text-sm transition-colors"
              style={{ color: active ? '#fff' : theme.colors.textMuted }}
            >
              {label}
            </span>
            {locked && (
              <Lock className="w-3 h-3 text-slate-600" />
            )}
          </div>
          {description && (
            <p className="font-exo2 text-[11px] text-slate-500 mt-0.5 leading-tight">
              {description}
            </p>
          )}
        </div>

        {/* Status Indicator */}
        <div className="flex items-center">
          {active ? (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-3 h-3 rounded-full"
              style={{ 
                backgroundColor: color,
                boxShadow: `0 0 12px ${color}, 0 0 20px ${color}50`
              }}
            />
          ) : (
            <ChevronRight 
              className="w-4 h-4 transition-transform" 
              style={{ 
                color: theme.colors.textMuted,
                transform: isHovered ? 'translateX(2px)' : 'translateX(0)'
              }}
            />
          )}
        </div>
      </div>
    </motion.button>
  );
};

// Premium 3D Zoom Slider with AI Clarity
const Zoom3DSlider = ({ value, onChange, max, isUnlocked, aiClarityEnabled, onToggleAIClarity, clarityDetails }) => {
  const { theme } = useTheme();
  const percentage = ((value - 1) / (max - 1)) * 100;
  
  return (
    <div 
      className="relative p-5 rounded-2xl"
      style={{
        background: 'linear-gradient(145deg, rgba(30,35,50,0.9) 0%, rgba(15,20,35,0.95) 100%)',
        boxShadow: '0 10px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.05)'
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{
              background: 'linear-gradient(145deg, rgba(0,242,234,0.2) 0%, rgba(0,242,234,0.05) 100%)',
              boxShadow: `0 0 15px ${theme.colors.primary}30`
            }}
          >
            <Layers className="w-4 h-4" style={{ color: theme.colors.primary }} />
          </div>
          <span className="font-mono text-xs uppercase tracking-widest text-slate-500">
            Optical Zoom
          </span>
        </div>
        <div 
          className="px-3 py-1.5 rounded-lg font-mono text-lg font-bold"
          style={{
            background: `linear-gradient(145deg, ${theme.colors.primary}20 0%, ${theme.colors.primary}05 100%)`,
            color: theme.colors.primary,
            boxShadow: `0 0 20px ${theme.colors.primary}20`
          }}
        >
          {value.toFixed(1)}x
        </div>
      </div>

      {/* 3D Slider Track */}
      <div 
        className="relative h-3 rounded-full overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(20,25,40,0.6) 100%)',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)'
        }}
      >
        {/* Progress Fill */}
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{
            width: `${percentage}%`,
            background: `linear-gradient(90deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%)`,
            boxShadow: `0 0 20px ${theme.colors.primary}60, inset 0 1px 0 rgba(255,255,255,0.3)`
          }}
          layoutId="zoom-fill"
        />
        
        {/* Slider Thumb */}
        <motion.div
          className="absolute top-1/2 -translate-y-1/2 w-5 h-5 rounded-full"
          style={{
            left: `calc(${percentage}% - 10px)`,
            background: 'linear-gradient(145deg, #fff 0%, #ddd 100%)',
            boxShadow: '0 2px 10px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,1)'
          }}
        />
        
        {/* Hidden Input */}
        <input
          type="range"
          min="1"
          max={max}
          step="0.1"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          data-testid="zoom-slider"
        />
      </div>

      {/* AI Clarity Toggle */}
      <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">
              AI Clarity
            </span>
            {value >= 5 && aiClarityEnabled && (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-purple-500/20 text-purple-300">
                ACTIVE
              </span>
            )}
          </div>
          <button
            onClick={onToggleAIClarity}
            className={`w-10 h-5 rounded-full transition-all duration-300 ${aiClarityEnabled ? 'bg-purple-500' : 'bg-gray-600'}`}
            style={{
              boxShadow: aiClarityEnabled ? '0 0 15px rgba(168,85,247,0.5)' : 'inset 0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            <motion.div
              className="w-4 h-4 rounded-full bg-white shadow-lg"
              animate={{ x: aiClarityEnabled ? 22 : 2 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            />
          </button>
        </div>
        
        {/* Clarity Details when active */}
        {value >= 5 && aiClarityEnabled && clarityDetails && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-3 grid grid-cols-2 gap-2"
          >
            <div className="px-2 py-1.5 rounded-lg bg-black/20">
              <span className="font-mono text-[9px] text-slate-500 block">Sharpness</span>
              <span className="font-mono text-xs text-purple-300">{clarityDetails.sharpness}x</span>
            </div>
            <div className="px-2 py-1.5 rounded-lg bg-black/20">
              <span className="font-mono text-[9px] text-slate-500 block">Contrast</span>
              <span className="font-mono text-xs text-purple-300">{clarityDetails.contrast}x</span>
            </div>
          </motion.div>
        )}
        
        <p className="font-mono text-[9px] text-slate-600 mt-2">
          Auto-enhances clarity at 5x+ zoom
        </p>
      </div>
    </div>
  );
};

// Main 3D Control Panel
const Pro3DControlPanel = ({ 
  filters, 
  toggleFilter, 
  isPremium, 
  isOwner,
  zoomLevel,
  setZoomLevel,
  MAX_ZOOM,
  activeFiltersCount,
  // AI Features
  aiClarityEnabled,
  onToggleAIClarity,
  clarityDetails,
  isAnomalyTrackingOn,
  onToggleAnomalyTracking,
  trackingSensitivity,
  onSetTrackingSensitivity,
  trackedAnomalies,
  frameAnalysis
}) => {
  const { theme } = useTheme();
  const isUnlocked = isPremium || isOwner;

  const freeFilters = [
    { id: 'sun_dimming', icon: Sun, label: 'Solar Shield', color: '#ff6b00', description: 'Super dim for sun observation' },
    { id: 'night_mode', icon: Moon, label: 'Night Mode', color: '#8b5cf6', description: 'Enhanced dark visibility' },
    { id: 'noise_reduction', icon: Sparkles, label: 'Crystal Clarity', color: '#00f2ea', description: 'Ultra-sharp footage' },
  ];

  const premiumFilters = [
    { id: 'star_enhancement', icon: Star, label: 'Star Amplifier', color: '#ffd700', description: 'Boost star visibility 10x' },
    { id: 'anomaly_highlight', icon: AlertTriangle, label: 'Anomaly Scanner', color: '#ff2e2e', description: 'AI anomaly detection' },
    { id: '4k_upscale', icon: ZoomIn, label: 'Ultra HD 4K', color: '#00f2ea', description: '4K resolution upscale' },
    { id: 'night_vision', icon: Eye, label: 'Night Vision', color: '#39ff14', description: 'Military-grade vision' },
    { id: 'infrared', icon: Radio, label: 'Infrared Scan', color: '#ff6600', description: 'Heat signature detection' },
    { id: 'thermal', icon: Thermometer, label: 'Thermal Imaging', color: '#ff3366', description: 'Temperature mapping' },
    { id: 'motion_enhance', icon: Video, label: 'Motion Amplify', color: '#a855f7', description: 'Detect subtle movement' },
    { id: 'object_tracking', icon: Target, label: 'AI Tracking', color: '#22c55e', description: 'Lock & track objects' },
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden" data-testid="filters-panel">
      {/* Premium Header */}
      <div 
        className="px-5 py-4"
        style={{
          background: 'linear-gradient(180deg, rgba(20,25,40,0.9) 0%, rgba(10,15,30,0.8) 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.05)'
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                background: `linear-gradient(145deg, ${theme.colors.primary}30 0%, ${theme.colors.primary}10 100%)`,
                boxShadow: `0 4px 15px ${theme.colors.primary}30, inset 0 1px 0 rgba(255,255,255,0.1)`
              }}
            >
              <Gauge className="w-5 h-5" style={{ color: theme.colors.primary }} />
            </div>
            <div>
              <h2 className="font-unbounded font-bold text-white text-sm">CONTROL CENTER</h2>
              <p className="font-mono text-[10px] text-slate-500">SKYBOT PRO v2.0</p>
            </div>
          </div>
          {activeFiltersCount > 0 && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="px-3 py-1.5 rounded-full font-mono text-xs font-bold"
              style={{
                background: `linear-gradient(145deg, ${theme.colors.primary}30 0%, ${theme.colors.primary}10 100%)`,
                color: theme.colors.primary,
                boxShadow: `0 4px 15px ${theme.colors.primary}20`
              }}
            >
              {activeFiltersCount} ACTIVE
            </motion.div>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-5">
        {/* Zoom Control with AI Clarity */}
        <Zoom3DSlider 
          value={zoomLevel}
          onChange={setZoomLevel}
          max={MAX_ZOOM}
          isUnlocked={isUnlocked}
          aiClarityEnabled={aiClarityEnabled}
          onToggleAIClarity={onToggleAIClarity}
          clarityDetails={clarityDetails}
        />

        {/* AI Anomaly Tracking Section */}
        <div 
          className="relative p-4 rounded-2xl"
          style={{
            background: 'linear-gradient(145deg, rgba(30,35,50,0.9) 0%, rgba(15,20,35,0.95) 100%)',
            boxShadow: '0 10px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
            border: isAnomalyTrackingOn ? '1px solid rgba(34,197,94,0.3)' : '1px solid rgba(255,255,255,0.05)'
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{
                  background: isAnomalyTrackingOn 
                    ? 'linear-gradient(145deg, rgba(34,197,94,0.3) 0%, rgba(34,197,94,0.1) 100%)'
                    : 'linear-gradient(145deg, rgba(50,55,70,0.8) 0%, rgba(30,35,50,0.8) 100%)',
                  boxShadow: isAnomalyTrackingOn ? '0 0 15px rgba(34,197,94,0.4)' : 'none'
                }}
              >
                <Crosshair className="w-4 h-4" style={{ color: isAnomalyTrackingOn ? '#22c55e' : '#64748b' }} />
              </div>
              <div>
                <span className="font-mono text-[11px] uppercase tracking-wider text-slate-400 block">
                  AI Tracking
                </span>
                {isAnomalyTrackingOn && (
                  <span className="font-mono text-[9px] text-green-400">
                    {trackedAnomalies?.length || 0} objects detected
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onToggleAnomalyTracking}
              className={`px-3 py-1.5 rounded-lg font-mono text-xs font-bold transition-all ${
                isAnomalyTrackingOn 
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                  : 'bg-slate-700/50 text-slate-400 border border-slate-600/30 hover:bg-slate-600/50'
              }`}
              style={{
                boxShadow: isAnomalyTrackingOn ? '0 0 15px rgba(34,197,94,0.3)' : 'none'
              }}
            >
              {isAnomalyTrackingOn ? 'ACTIVE' : 'START'}
            </button>
          </div>

          {/* Sensitivity Slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[9px] text-slate-500">Sensitivity</span>
              <span className="font-mono text-[10px] text-slate-400">{Math.round(trackingSensitivity * 100)}%</span>
            </div>
            <input
              type="range"
              min="0.1"
              max="1"
              step="0.1"
              value={trackingSensitivity}
              onChange={(e) => onSetTrackingSensitivity(parseFloat(e.target.value))}
              className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(90deg, #22c55e ${trackingSensitivity * 100}%, rgba(100,116,139,0.3) ${trackingSensitivity * 100}%)`
              }}
            />
          </div>

          {/* Frame Analysis Display */}
          {isAnomalyTrackingOn && frameAnalysis && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-3 p-2 rounded-lg bg-black/30"
            >
              <p className="font-mono text-[10px] text-slate-300">{frameAnalysis}</p>
            </motion.div>
          )}

          <p className="font-mono text-[9px] text-slate-600 mt-2">
            Real-time detection & tracking of sky anomalies
          </p>
        </div>

        {/* Standard Filters Section */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 px-1">
            <Zap className="w-4 h-4 text-green-400" />
            <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">
              Standard Filters
            </span>
          </div>
          {freeFilters.map((filter) => (
            <Filter3DCard
              key={filter.id}
              icon={filter.icon}
              label={filter.label}
              description={filter.description}
              active={filters[filter.id]}
              locked={false}
              onClick={() => toggleFilter(filter.id)}
              color={filter.color}
            />
          ))}
        </div>

        {/* Premium Filters Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-2">
              <Crown className="w-4 h-4 text-yellow-400" />
              <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">
                Premium Filters
              </span>
            </div>
            {!isUnlocked && (
              <span 
                className="font-mono text-[9px] px-2 py-1 rounded-full"
                style={{
                  background: 'rgba(255,215,0,0.1)',
                  color: '#ffd700',
                  border: '1px solid rgba(255,215,0,0.2)'
                }}
              >
                UPGRADE TO UNLOCK
              </span>
            )}
          </div>
          {premiumFilters.map((filter) => (
            <Filter3DCard
              key={filter.id}
              icon={filter.icon}
              label={filter.label}
              description={filter.description}
              active={filters[filter.id]}
              locked={!isUnlocked}
              onClick={() => toggleFilter(filter.id)}
              color={filter.color}
            />
          ))}
        </div>
      </div>

      {/* Premium Footer */}
      <div 
        className="px-5 py-4"
        style={{
          background: 'linear-gradient(180deg, rgba(10,15,30,0.8) 0%, rgba(5,10,25,0.95) 100%)',
          borderTop: '1px solid rgba(255,255,255,0.05)'
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className={`w-2 h-2 rounded-full ${isUnlocked ? 'bg-green-400' : 'bg-yellow-400'}`}
              style={{ boxShadow: isUnlocked ? '0 0 10px #4ade80' : '0 0 10px #facc15' }}
            />
            <span className="font-mono text-xs text-slate-400">
              {isOwner ? 'OWNER ACCESS' : isPremium ? 'PREMIUM ACTIVE' : 'STANDARD MODE'}
            </span>
          </div>
          <span className="font-mono text-[10px]" style={{ color: theme.colors.primary }}>
            GPT-5.2 • 4K ULTRA
          </span>
        </div>
      </div>
    </div>
  );
};

export default Pro3DControlPanel;
